import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Select,
  Typography,
  Space,
  Spin,
  Empty,
  Divider
} from 'antd';
import {
  UserOutlined,
  CarOutlined,
  ToolOutlined,
  ProjectOutlined,
  FileTextOutlined,
  TeamOutlined,
  DashboardOutlined
} from '@ant-design/icons';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line
} from 'recharts';
import { collection, onSnapshot, getDocs } from 'firebase/firestore';
import { firestore } from '../../firebase';
import { useAuth } from '../Context';
import moment from 'moment';

const { Title } = Typography;
const { Option } = Select;

// Colores para los gráficos
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

const SucursalesAnalytics = () => {
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [sucursales, setSucursales] = useState([]);
  const [selectedSucursal, setSelectedSucursal] = useState('todas');
  const [analytics, setAnalytics] = useState({
    empleados: [],
    vehiculos: [],
    equipos: [],
    proyectos: [],
    solicitudes: [],
    resumenGeneral: {
      totalEmpleados: 0,
      totalVehiculos: 0,
      totalEquipos: 0,
      totalProyectos: 0,
      totalSolicitudes: 0
    }
  });

  // Cargar lista de sucursales
  useEffect(() => {
    const loadSucursales = async () => {
      try {
        const sucursalesSnapshot = await getDocs(collection(firestore, 'sucursal'));
        const sucursalesData = sucursalesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setSucursales(sucursalesData);
      } catch (error) {
        console.error('Error al cargar sucursales:', error);
      }
    };

    if (isAdmin) {
      loadSucursales();
    }
  }, [isAdmin]);

  // Cargar datos analíticos
  useEffect(() => {
    if (!isAdmin || sucursales.length === 0) return;

    setLoading(true);
    const unsubscribes = [];

    const loadAnalytics = async () => {
      const newAnalytics = {
        empleados: [],
        vehiculos: [],
        equipos: [],
        proyectos: [],
        solicitudes: [],
        resumenGeneral: {
          totalEmpleados: 0,
          totalVehiculos: 0,
          totalEquipos: 0,
          totalProyectos: 0,
          totalSolicitudes: 0
        }
      };

      const sucursalesToProcess = selectedSucursal === 'todas' 
        ? sucursales 
        : sucursales.filter(s => s.id === selectedSucursal);

      for (const sucursal of sucursalesToProcess) {
        
        try {
          // Empleados
          const empleadosRef = collection(firestore, `sucursal/${sucursal.id}/empleado`);
          const empleadosUnsub = onSnapshot(empleadosRef, (snapshot) => {
            const activos = snapshot.docs.filter(doc => {
              const data = doc.data();
              return data.estatus === 'Activo';
            }).length;
            const inactivos = snapshot.docs.filter(doc => {
              const data = doc.data();
              return data.estatus === 'Inactivo';
            }).length;
            
            newAnalytics.empleados = newAnalytics.empleados.filter(e => e.sucursal !== sucursal.id);
            newAnalytics.empleados.push({
              sucursal: sucursal.id,
              nombre: sucursal.id,
              activos,
              inactivos,
              total: activos + inactivos
            });
            
            updateAnalytics({ ...newAnalytics });
          }, (error) => {
            console.error(`Error al consultar empleados de ${sucursal.id}:`, error);
          });
          unsubscribes.push(empleadosUnsub);

          // Vehículos
          const vehiculosRef = collection(firestore, `sucursal/${sucursal.id}/vehiculos`);
          const vehiculosUnsub = onSnapshot(vehiculosRef, (snapshot) => {
            
            const vehiculosData = snapshot.docs.map(doc => {
              const data = doc.data();
              return { id: doc.id, ...data };
            });
            
            // Contar todos independientemente del estado para debug
            const totalVehiculos = vehiculosData.length;
            
            // Filtrar por estado, siendo flexible con los valores
            const activos = vehiculosData.filter(vehiculo => {
              const estatus = vehiculo.estatus;
              // Considerar activo si es 'Activo', 'activo', o si no tiene estatus definido
              return estatus === 'Activo' || estatus === 'activo' || !estatus;
            }).length;
            
            const inactivos = vehiculosData.filter(vehiculo => {
              const estatus = vehiculo.estatus;
              return estatus === 'Inactivo' || estatus === 'inactivo';
            }).length;
            
            
            newAnalytics.vehiculos = newAnalytics.vehiculos.filter(v => v.sucursal !== sucursal.id);
            newAnalytics.vehiculos.push({
              sucursal: sucursal.id,
              nombre: sucursal.id,
              activos,
              inactivos,
              total: activos + inactivos
            });
            
            updateAnalytics({ ...newAnalytics });
          }, (error) => {
            console.error(`Error al consultar vehículos de ${sucursal.id}:`, error);
          });
          unsubscribes.push(vehiculosUnsub);

        // Equipos
        const equiposRef = collection(firestore, `sucursal/${sucursal.id}/equipo`);
        const equiposUnsub = onSnapshot(equiposRef, (snapshot) => {
          const activos = snapshot.docs.filter(doc => doc.data().estatus === 'Activo').length;
          const inactivos = snapshot.docs.filter(doc => doc.data().estatus === 'Inactivo').length;
          
          newAnalytics.equipos = newAnalytics.equipos.filter(e => e.sucursal !== sucursal.id);
          newAnalytics.equipos.push({
            sucursal: sucursal.id,
            nombre: sucursal.id,
            activos,
            inactivos,
            total: activos + inactivos
          });
          
          updateAnalytics({ ...newAnalytics });
        });
        unsubscribes.push(equiposUnsub);

        // Proyectos
        const proyectosRef = collection(firestore, `sucursal/${sucursal.id}/proyecto`);
        const proyectosUnsub = onSnapshot(proyectosRef, (snapshot) => {
          const enProceso = snapshot.docs.filter(doc => doc.data().estatus === 'En proceso').length;
          const terminados = snapshot.docs.filter(doc => doc.data().estatus === 'Terminado').length;
          const cancelados = snapshot.docs.filter(doc => doc.data().estatus === 'Cancelado').length;
          
          newAnalytics.proyectos = newAnalytics.proyectos.filter(p => p.sucursal !== sucursal.id);
          newAnalytics.proyectos.push({
            sucursal: sucursal.id,
            nombre: sucursal.id,
            enProceso,
            terminados,
            cancelados,
            total: enProceso + terminados + cancelados
          });
          
          updateAnalytics({ ...newAnalytics });
        });
        unsubscribes.push(proyectosUnsub);

        // Solicitudes
        const solicitudesRef = collection(firestore, `sucursal/${sucursal.id}/solicitud`);
        const solicitudesUnsub = onSnapshot(solicitudesRef, (snapshot) => {
          const pendientes = snapshot.docs.filter(doc => doc.data().estatus === 'Pendiente').length;
          const aprobadas = snapshot.docs.filter(doc => doc.data().estatus === 'Aprobada').length;
          const rechazadas = snapshot.docs.filter(doc => doc.data().estatus === 'Rechazada').length;
          
          newAnalytics.solicitudes = newAnalytics.solicitudes.filter(s => s.sucursal !== sucursal.id);
          newAnalytics.solicitudes.push({
            sucursal: sucursal.id,
            nombre: sucursal.id,
            pendientes,
            aprobadas,
            rechazadas,
            total: pendientes + aprobadas + rechazadas
          });
          
          updateAnalytics({ ...newAnalytics });
        });
        unsubscribes.push(solicitudesUnsub);
        } catch (error) {
          console.error(`Error al procesar sucursal ${sucursal.id}:`, error);
        }
      }
    };

    const updateAnalytics = (data) => {
      
      // Calcular resumen general de forma más segura
      const resumenGeneral = {
        totalEmpleados: (data.empleados || []).reduce((sum, item) => sum + (item.total || 0), 0),
        totalVehiculos: (data.vehiculos || []).reduce((sum, item) => sum + (item.total || 0), 0),
        totalEquipos: (data.equipos || []).reduce((sum, item) => sum + (item.total || 0), 0),
        totalProyectos: (data.proyectos || []).reduce((sum, item) => sum + (item.total || 0), 0),
        totalSolicitudes: (data.solicitudes || []).reduce((sum, item) => sum + (item.total || 0), 0)
      };

      
      setAnalytics({ ...data, resumenGeneral });
      setLoading(false);
    };

    loadAnalytics();

    return () => {
      unsubscribes.forEach(unsub => unsub());
    };
  }, [isAdmin, sucursales, selectedSucursal]);

  // Función para renderizar gráfico de pie
  const renderPieChart = (data, dataKey, title) => (
    <ResponsiveContainer width="100%" height={300}>
      <PieChart>
        <Pie
          data={data}
          cx="50%"
          cy="50%"
          labelLine={false}
          label={({ nombre, value }) => `${nombre}: ${value}`}
          outerRadius={80}
          fill="#8884d8"
          dataKey={dataKey}
        >
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
          ))}
        </Pie>
        <Tooltip />
        <Legend />
      </PieChart>
    </ResponsiveContainer>
  );

  // Función para renderizar gráfico de barras
  const renderBarChart = (data, title) => (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="nombre" />
        <YAxis />
        <Tooltip />
        <Legend />
        <Bar dataKey="activos" stackId="a" fill="#52c41a" name="Activos" />
        <Bar dataKey="inactivos" stackId="a" fill="#f5222d" name="Inactivos" />
      </BarChart>
    </ResponsiveContainer>
  );

  if (!isAdmin) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Empty
          description="Acceso denegado. Solo los administradores pueden ver esta sección."
        />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>
          <DashboardOutlined /> Dashboard de Sucursales
        </Title>
        <Space>
          <span>Filtrar por sucursal:</span>
          <Select
            value={selectedSucursal}
            onChange={setSelectedSucursal}
            style={{ width: 200 }}
          >
            <Option value="todas">Todas las sucursales</Option>
            {sucursales.map(sucursal => (
              <Option key={sucursal.id} value={sucursal.id}>
                {sucursal.id}
              </Option>
            ))}
          </Select>
        </Space>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 50 }}>
          <Spin size="large" tip="Cargando datos analíticos..." />
        </div>
      ) : (
        <>
          {/* Resumen General */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col span={24}>
              <Card>
                <Title level={4}>Resumen General</Title>
                <Row gutter={16}>
                  <Col span={5}>
                    <Statistic
                      title="Total Empleados"
                      value={analytics.resumenGeneral.totalEmpleados}
                      prefix={<UserOutlined />}
                    />
                  </Col>
                  <Col span={5}>
                    <Statistic
                      title="Total Vehículos"
                      value={analytics.resumenGeneral.totalVehiculos}
                      prefix={<CarOutlined />}
                    />
                  </Col>
                  <Col span={5}>
                    <Statistic
                      title="Total Equipos"
                      value={analytics.resumenGeneral.totalEquipos}
                      prefix={<ToolOutlined />}
                    />
                  </Col>
                  <Col span={5}>
                    <Statistic
                      title="Total Proyectos"
                      value={analytics.resumenGeneral.totalProyectos}
                      prefix={<ProjectOutlined />}
                    />
                  </Col>
                  <Col span={4}>
                    <Statistic
                      title="Total Solicitudes"
                      value={analytics.resumenGeneral.totalSolicitudes}
                      prefix={<FileTextOutlined />}
                    />
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>

          {/* Gráficos de distribución */}
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Card title="Empleados por Sucursal">
                {analytics.empleados.length > 0 ? (
                  renderPieChart(analytics.empleados, 'total', 'Empleados')
                ) : (
                  <Empty description="No hay datos de empleados" />
                )}
              </Card>
            </Col>
            <Col span={12}>
              <Card title="Vehículos por Sucursal">
                {analytics.vehiculos.length > 0 ? (
                  renderPieChart(analytics.vehiculos, 'total', 'Vehículos')
                ) : (
                  <Empty description="No hay datos de vehículos" />
                )}
              </Card>
            </Col>
          </Row>

          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col span={12}>
              <Card title="Equipos por Sucursal">
                {analytics.equipos.length > 0 ? (
                  renderPieChart(analytics.equipos, 'total', 'Equipos')
                ) : (
                  <Empty description="No hay datos de equipos" />
                )}
              </Card>
            </Col>
            <Col span={12}>
              <Card title="Proyectos por Sucursal">
                {analytics.proyectos.length > 0 ? (
                  renderPieChart(analytics.proyectos, 'total', 'Proyectos')
                ) : (
                  <Empty description="No hay datos de proyectos" />
                )}
              </Card>
            </Col>
          </Row>

          {/* Gráficos de estado */}
          <Divider />
          <Title level={3}>Análisis por Estados</Title>

          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Card title="Estado de Empleados">
                {analytics.empleados.length > 0 ? (
                  renderBarChart(analytics.empleados, 'Estado de Empleados')
                ) : (
                  <Empty description="No hay datos de empleados" />
                )}
              </Card>
            </Col>
            <Col span={12}>
              <Card title="Estado de Vehículos">
                {analytics.vehiculos.length > 0 ? (
                  renderBarChart(analytics.vehiculos, 'Estado de Vehículos')
                ) : (
                  <Empty description="No hay datos de vehículos" />
                )}
              </Card>
            </Col>
          </Row>

          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col span={12}>
              <Card title="Estado de Equipos">
                {analytics.equipos.length > 0 ? (
                  renderBarChart(analytics.equipos, 'Estado de Equipos')
                ) : (
                  <Empty description="No hay datos de equipos" />
                )}
              </Card>
            </Col>
            <Col span={12}>
              <Card title="Estado de Solicitudes">
                {analytics.solicitudes.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={analytics.solicitudes}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="nombre" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="pendientes" stackId="a" fill="#faad14" name="Pendientes" />
                      <Bar dataKey="aprobadas" stackId="a" fill="#52c41a" name="Aprobadas" />
                      <Bar dataKey="rechazadas" stackId="a" fill="#f5222d" name="Rechazadas" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Empty description="No hay datos de solicitudes" />
                )}
              </Card>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
};

export default SucursalesAnalytics;
